/**
 * Shared presence-aware peer indicators.
 *
 *   - useOfflinePeers(): reactive snapshot of MobyNode.offlinePeers backed by
 *     a single module-level interval (O(1) regardless of how many consumers).
 *   - usePeerUIState(peerId, connection): legacy hook — derives the flat 11-state
 *     PeerUIState using the authoritative presence signal + per-peer retry
 *     metadata. Re-runs on every parent render.
 *   - usePeerLifecycle(peerId): NEW event-driven hook — subscribes to
 *     `peerStateStore` and returns a stable snapshot/descriptor reference
 *     that only changes when the lifecycle/modifiers actually transition.
 *     Renders only on real state changes, not on every parent render or
 *     every Dexie contact mutation.
 *   - <PeerHealthDot />: the small colored circle rendered next to a peer
 *     avatar in the contacts drawer, chat list, and chat drawer. Memoized
 *     with React.memo so identity-stable props don't re-render the dot.
 *
 * See docs/connection-desync-audit.md — P0-C.
 * See docs/connection-state-machine.md — PR-2 (lifecycle hook wiring).
 */

import { memo, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { peerStateStore, type PeerStoreEvent } from '@/lib/peer-state-store';
import {
  derivePeerUIState,
  getPeerUIDescriptor,
  type PeerStateSnapshot,
  type PeerUIDescriptor,
  type PeerUIState,
  type RetryPhase,
} from '@/lib/peer-ui-state';
import {
  peerMetricsService,
  type DbHealthScore,
  type HealthScore,
  type MetricsUpdate,
} from '@/services/peer-metrics-service';

// ─── Offline-peer snapshot (single shared interval) ──────────────────────────

// Global mutable snapshot. React subscribers re-render via the tick counter.
let offlineSnapshot: ReadonlySet<string> = new Set();
let tick = 0;
const listeners = new Set<() => void>();
let pollerStarted = false;

const POLL_INTERVAL_MS = 3000;

function startPoller(): void {
  if (pollerStarted) return;
  pollerStarted = true;

  const poll = (): void => {
    try {
      const node = (globalThis as any).node;
      const next: Set<string> = node?.offlinePeers instanceof Set
        ? new Set(node.offlinePeers)
        : new Set();

      // Fast equality check: size + membership diff.
      let changed = next.size !== offlineSnapshot.size;
      if (!changed) {
        for (const addr of next) {
          if (!offlineSnapshot.has(addr)) { changed = true; break; }
        }
      }
      if (changed) {
        offlineSnapshot = next;
        tick++;
        listeners.forEach(fn => fn());
      }
    } catch {
      // Ignore — node may not be attached yet on early renders.
    }
  };

  poll();
  setInterval(poll, POLL_INTERVAL_MS);
}

/**
 * Reactive snapshot of the signal-server-confirmed offline peer set.
 *
 * Single interval shared across all consumers; each consumer pays only the
 * cost of a Set.has lookup at render.
 */
export function useOfflinePeers(): ReadonlySet<string> {
  const [, setLocalTick] = useState(tick);
  useEffect(() => {
    startPoller();
    const listener = () => setLocalTick(tick);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);
  return offlineSnapshot;
}

// ─── Per-peer state derivation hook ──────────────────────────────────────────

export interface PeerConnectionSnapshot {
  state?: RTCPeerConnectionState;
  iceState?: RTCIceConnectionState;
  healthScore?: 'green' | 'yellow' | 'red' | 'gray';
  rtt?: number;
  /**
   * Smoothed jitter from the most recent stats poll. Persisted in Dexie
   * alongside `rtt` (see provider.tsx:7614) and surfaces in the
   * `connected-direct` tooltip when present. `undefined` when no jitter
   * sample has been taken (first ~3 RTT polls — see provider.tsx:7456).
   */
  jitter?: number;
}

export interface PeerRuntimeMeta {
  retryPhase?: RetryPhase;
  reconnectionAttempts?: number;
  chatChannelOpen?: boolean;
  connectionType?: 'direct' | 'relay' | 'unknown';
}

/**
 * Lightweight hook for call-sites that only have the persisted
 * `contact.connection` snapshot (chat list / contact list avatars).
 *
 * Pulls retry metadata and channel state directly from globalThis.node.peers
 * when available; falls back to the persisted snapshot otherwise.
 */
export function usePeerUIState(
  peerId: string,
  connection?: PeerConnectionSnapshot,
): { uiState: PeerUIState; descriptor: ReturnType<typeof getPeerUIDescriptor> } {
  const offlinePeers = useOfflinePeers();
  const isOfflineInPresence = offlinePeers.has(peerId);

  // Pull live peer runtime meta if the node has it — saves us from needing
  // to thread retryPhase/attempts through Dexie.
  const meta = readPeerRuntimeMeta(peerId);

  const uiState = derivePeerUIState({
    rtcState: connection?.state ?? meta.rtcStateLive,
    iceState: connection?.iceState ?? meta.iceStateLive,
    isOfflineInPresence,
    retryPhase: meta.retryPhase,
    reconnectionAttempts: meta.reconnectionAttempts,
    chatChannelOpen: meta.chatChannelOpen,
    connectionType: meta.connectionType,
  });

  const descriptor = getPeerUIDescriptor(uiState, {
    rtt: connection?.rtt,
    jitter: connection?.jitter,
    reconnectionAttempts: meta.reconnectionAttempts,
    retryPhase: meta.retryPhase,
    healthScore: connection?.healthScore,
  });

  return { uiState, descriptor };
}

interface RuntimeMeta extends PeerRuntimeMeta {
  rtcStateLive?: RTCPeerConnectionState;
  iceStateLive?: RTCIceConnectionState;
}

function readPeerRuntimeMeta(peerId: string): RuntimeMeta {
  try {
    const node = (globalThis as any).node;
    const peer = node?.peers?.get?.(peerId);
    if (!peer) return {};
    return {
      retryPhase: peer.retryPhase,
      reconnectionAttempts: peer.reconnectionAttempts,
      chatChannelOpen: peer.chat?.channel?.readyState === 'open',
      rtcStateLive: peer.connection?.connectionState,
      iceStateLive: peer.connection?.iceConnectionState,
      // connectionType requires getStats() which the badge already polls —
      // not plumbed here to avoid double-polling.
    };
  } catch {
    return {};
  }
}

// ─── Lifecycle-store subscription hook (event-driven, memoized) ──────────────

/**
 * Result shape returned by `usePeerLifecycle`.
 *
 * `snapshot` is a stable reference (the same object) across renders where
 * nothing about the peer's lifecycle/modifiers changed — this is what makes
 * `React.memo`-wrapped consumers actually skip re-rendering. `descriptor`
 * is recomputed only when `uiStateRaw` or the relevant context fields
 * change.
 */
export interface PeerLifecycleResult {
  /** Current snapshot from the store, or `null` if the peer isn't attached. */
  snapshot: PeerStateSnapshot | null;
  /** Pre-derived UI descriptor for the snapshot — saves consumer work. */
  descriptor: PeerUIDescriptor;
}

/**
 * Module-level cache mapping peerId → last-rendered descriptor. Lets
 * `usePeerLifecycle` return a stable result reference when neither the
 * snapshot's `uiStateRaw` nor the descriptor-relevant modifiers changed,
 * even across remounts of consumer components.
 *
 * The cache key includes `localBusy` because the descriptor color flips
 * between solid green and idle green based on it; a peer transitioning
 * busy → idle without changing `uiStateRaw` still needs a fresh
 * descriptor. Other modifiers (relay, throttled) are scaffolded for
 * future descriptor variants but don't drive the cache key today.
 *
 * Bounded by the live attached-peer count — the store cleans up via
 * `detach()`, and we evict entries lazily when a snapshot read returns
 * undefined for a previously-cached peerId.
 */
interface DescriptorCacheEntry {
  uiStateRaw: PeerUIState;
  localBusy: boolean;
  result: PeerLifecycleResult;
}
const descriptorCache = new Map<string, DescriptorCacheEntry>();

const FALLBACK_DESCRIPTOR: PeerUIDescriptor = getPeerUIDescriptor('initializing');
const FALLBACK_RESULT: PeerLifecycleResult = {
  snapshot: null,
  descriptor: FALLBACK_DESCRIPTOR,
};

/**
 * Subscribe a React component to a peer's lifecycle in `peerStateStore`.
 *
 * Re-render contract:
 *   - First render: returns either the cached descriptor (if the store has
 *     a snapshot) or the fallback `'initializing'` descriptor.
 *   - Subsequent renders: returns the **same reference** as the previous
 *     render unless the peer's `uiStateRaw` actually changed. `React.memo`
 *     on the consumer will then short-circuit the render entirely.
 *
 * Why `useSyncExternalStore`:
 *   - Concurrent-safe: the snapshot we render on tearing-prone code paths
 *     (Suspense, transitions) matches what we subscribed to.
 *   - Single subscription per consumer — no manual `useEffect` plumbing.
 *
 * Why module-level cache:
 *   - Stable references across remounts. Without it, a chat-list scroll
 *     that unmounts/remounts a row would force a fresh descriptor object,
 *     defeating `React.memo`.
 */
export function usePeerLifecycle(peerId: string): PeerLifecycleResult {
  // Subscribe lambda: peerStateStore emits typed events when lifecycle or
  // modifiers actually change. We only re-run getSnapshot when an event
  // fires for OUR peerId — events for other peers should not trigger a
  // re-render in this consumer.
  const subscribe = useCallback(
    (notify: () => void) => {
      const handler = (evt: PeerStoreEvent) => {
        if (evt.peerId === peerId) notify();
      };
      const unsubState = peerStateStore.subscribe('peer:state-change', handler);
      const unsubMod = peerStateStore.subscribe('peer:modifier-change', handler);
      const unsubEnd = peerStateStore.subscribe('peer:lifecycle-end', handler);
      return () => {
        unsubState();
        unsubMod();
        unsubEnd();
      };
    },
    [peerId],
  );

  // getSnapshot: read the current snapshot, return a memoized result that
  // is referentially stable when uiStateRaw + descriptor-relevant modifiers
  // haven't changed.
  const getSnapshot = useCallback((): PeerLifecycleResult => {
    const snap = peerStateStore.getPeer(peerId);
    if (!snap) {
      // Peer not attached yet — drop any stale cache entry and return the
      // shared fallback so identity-stable parents don't re-render.
      descriptorCache.delete(peerId);
      return FALLBACK_RESULT;
    }

    const localBusy = snap.modifiers.localBusy;
    const cached = descriptorCache.get(peerId);
    if (
      cached &&
      cached.uiStateRaw === snap.uiStateRaw &&
      cached.localBusy === localBusy &&
      cached.result.snapshot === snap
    ) {
      return cached.result;
    }

    // Build the descriptor once per (uiStateRaw, localBusy) change. We
    // don't have RTT or health-score context here (the store doesn't track
    // them); descriptor consumers that need those still use
    // `usePeerUIState` for now.
    const descriptor = getPeerUIDescriptor(snap.uiStateRaw, { localBusy });
    const result: PeerLifecycleResult = { snapshot: snap, descriptor };
    descriptorCache.set(peerId, { uiStateRaw: snap.uiStateRaw, localBusy, result });
    return result;
  }, [peerId]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// ─── Worker-metrics subscription hook ────────────────────────────────────────

/**
 * Live snapshot of the worker's metrics for a peer. All fields are nullable
 * because the worker may not have computed them yet (peer just registered,
 * or `collectStats()` hasn't fired its first batch).
 *
 * Sourced from `peerMetricsService` which fans out periodic `metrics-update`
 * messages from the singleton metrics worker (500ms during active transfer,
 * 5s while idle). Cached on the main thread side so first render reads
 * synchronously via `getLatestMetrics()` — no flash of empty state.
 */
export interface PeerMetricsSnapshot {
  /** Round-trip time in milliseconds. `null` when the worker has no measurement yet. */
  rtt: number | null;
  /**
   * Smoothed jitter in milliseconds (variance of recent RTT samples).
   * `null` when the worker hasn't accumulated enough samples yet — the
   * smoothing pipeline at provider.tsx:7437-7460 needs at least 3 samples
   * before it produces a value, so newly-attached peers report null even
   * after RTT becomes available.
   */
  jitter: number | null;
  /** Hysteresis-smoothed health score for badge color. `null` when not yet computed. */
  healthScore: DbHealthScore | null;
  /** Coarse quality classification. `null` when not yet computed. */
  quality: HealthScore | null;
  /** Per-transfer bandwidth in bytes/sec. `null` when no transfer is active. */
  bandwidth: number | null;
  /** Total cross-transfer bandwidth in bytes/sec. `null` when no transfer is active. */
  totalBandwidth: number | null;
}

const EMPTY_METRICS: PeerMetricsSnapshot = {
  rtt: null,
  jitter: null,
  healthScore: null,
  quality: null,
  bandwidth: null,
  totalBandwidth: null,
};

function metricsToSnapshot(m: MetricsUpdate | null): PeerMetricsSnapshot {
  if (!m) return EMPTY_METRICS;
  return {
    // RTT=0 is the worker's initial-state sentinel (never a real measurement),
    // so treat it as "no data" — descriptor consumers should fall back to
    // the persisted Dexie snapshot or render N/A.
    rtt: m.rtt > 0 ? m.rtt : null,
    // Jitter=0 is the worker's initial-state sentinel as well. The smoothing
    // pipeline (80/20 EMA, capped at 50% of median RTT) guarantees jitter
    // becomes positive once measured, so 0 reliably means "no data yet".
    jitter: m.jitter > 0 ? m.jitter : null,
    healthScore: m.dbHealthScore ?? null,
    quality: m.quality ?? null,
    bandwidth: m.bandwidth > 0 ? m.bandwidth : null,
    totalBandwidth: m.totalBandwidth > 0 ? m.totalBandwidth : null,
  };
}

/**
 * Subscribe a React component to a peer's worker-computed metrics.
 *
 * Why a separate hook from `usePeerLifecycle`:
 *   - Different update cadence: lifecycle changes on RTC/channel events
 *     (event-driven), metrics arrive on a 500ms/5s timer (poll-driven).
 *   - Different source of truth: lifecycle is `peerStateStore` (ICE +
 *     channel state); metrics are `peerMetricsService` (worker + getStats).
 *   - Keeps `usePeerLifecycle`'s descriptor cache stable — that hook's
 *     `useSyncExternalStore` returns referentially-stable results across
 *     renders where lifecycle hasn't changed; mixing fast metrics into
 *     it would force every 500ms tick to invalidate the cache and force
 *     consumer re-renders even when the only change is a 1ms RTT drift.
 *
 * Re-render contract:
 *   - First render: returns the worker's last cached metrics snapshot
 *     (via `getLatestMetrics`) or `EMPTY_METRICS` if the peer hasn't
 *     been registered yet. No async wait.
 *   - Subsequent renders: re-renders the consumer when the worker emits
 *     a new `metrics-update` for this peer.
 *
 * Graceful degradation when the worker isn't available:
 *   - `peerMetricsService.onMetricsUpdate` returns a no-op unsubscribe
 *     and the callback never fires. The hook stays at `EMPTY_METRICS`.
 *   - Descriptor consumers should fall back to `connection?.rtt` from
 *     the persisted Dexie snapshot — see `PeerHealthDotInner` below.
 */
export function usePeerMetrics(peerId: string): PeerMetricsSnapshot {
  const [snapshot, setSnapshot] = useState<PeerMetricsSnapshot>(() =>
    metricsToSnapshot(peerMetricsService.getLatestMetrics(peerId)),
  );

  useEffect(() => {
    // Reseed on peerId change — the previous peer's snapshot is stale for
    // the new peer and would otherwise leak into the first render.
    setSnapshot(metricsToSnapshot(peerMetricsService.getLatestMetrics(peerId)));

    const unsub = peerMetricsService.onMetricsUpdate(peerId, (m) => {
      setSnapshot(metricsToSnapshot(m));
    });
    return unsub;
  }, [peerId]);

  return snapshot;
}

// ─── Shared dot component (memoized) ─────────────────────────────────────────

interface PeerHealthDotProps {
  peerId: string;
  connection?: PeerConnectionSnapshot;
  size?: 'sm' | 'md';
}

function PeerHealthDotInner({ peerId, connection, size = 'md' }: PeerHealthDotProps) {
  // Prefer the lifecycle store when it has a snapshot for this peer —
  // event-driven, no per-render derivation. Fall back to `usePeerUIState`
  // when the store hasn't seen this peer yet (e.g. unattached contact in
  // the contact list before the first connection attempt) so existing
  // contacts still render a meaningful dot from their persisted snapshot.
  const lifecycle = usePeerLifecycle(peerId);
  const fallback = usePeerUIState(peerId, connection);
  // Subscribe to the worker's live metrics so the tooltip shows fresh
  // RTT / healthScore even when the lifecycle snapshot itself hasn't
  // changed (modifiers don't track RTT — see usePeerMetrics docstring).
  const metrics = usePeerMetrics(peerId);

  // Build the descriptor merging all three sources:
  //   1. lifecycle.snapshot — authoritative state + localBusy modifier
  //   2. metrics — worker-computed RTT + hysteresis-smoothed healthScore
  //      (preferred over the persisted Dexie snapshot because the worker
  //      applies 3-consecutive-readings hysteresis that prevents the
  //      badge from flickering on transient blips).
  //   3. connection prop — Dexie-backed fallback for RTT / healthScore
  //      when the worker hasn't computed them yet (e.g. fresh peer
  //      attach, worker spawn failed, or stats poll suspended during
  //      large transfer — see provider.tsx:7387).
  //
  // Pre-fix bug (5/3 W10 Edge): when lifecycle.snapshot existed, the
  // descriptor came directly from `lifecycle.descriptor` which was built
  // with `{ localBusy }` only — RTT and healthScore were silently
  // discarded, so the tooltip always showed "RTT N/A" regardless of
  // actual measurements. See docs/connection-desync-audit.md 5/3 §RTT.
  const descriptor = lifecycle.snapshot
    ? getPeerUIDescriptor(lifecycle.snapshot.uiStateRaw, {
        localBusy: lifecycle.snapshot.modifiers.localBusy,
        rtt: metrics.rtt ?? connection?.rtt,
        jitter: metrics.jitter ?? connection?.jitter,
        healthScore: metrics.healthScore ?? connection?.healthScore,
      })
    : fallback.descriptor;

  const sizeClass = size === 'sm'
    ? 'size-2.5 -bottom-0.5 -right-0.5 border-2 border-surface-container-high'
    : 'size-3 -bottom-1 -right-1 border-2 border-surface';

  // Memoize the inline style object so identical-color renders don't churn
  // the DOM — important when this dot lives inside a memoized parent and
  // we want the entire subtree to short-circuit reconciliation.
  const dotStyle = useMemo(() => ({ backgroundColor: descriptor.color }), [descriptor.color]);

  return (
    <div
      className={`absolute rounded-full ${sizeClass}`}
      // eslint-disable-next-line style/no-inline-styles -- dynamic color from state descriptor
      style={dotStyle}
      title={descriptor.tooltip}
    />
  );
}

/**
 * `React.memo` with a custom comparator so the dot only re-renders when
 * something the consumer actually controls changes:
 *
 *   - peerId — different peer, must re-subscribe.
 *   - size — visual prop.
 *   - connection.state / connection.iceState / connection.healthScore /
 *     connection.rtt — these flow into the legacy fallback path. The
 *     `useLiveQuery` in chat-list-panel rebuilds `contact.connection`
 *     into a fresh object every Dexie tick, so a shallow ref check would
 *     force re-renders even when no state field changed. Field-level
 *     comparison fixes that.
 *
 * The lifecycle hook itself returns referentially stable results, so
 * inside the component the actual descriptor swap only happens on real
 * lifecycle/modifier transitions.
 */
export const PeerHealthDot = memo(PeerHealthDotInner, (prev, next) => {
  if (prev.peerId !== next.peerId) return false;
  if (prev.size !== next.size) return false;
  const a = prev.connection;
  const b = next.connection;
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.state === b.state &&
    a.iceState === b.iceState &&
    a.healthScore === b.healthScore &&
    a.rtt === b.rtt &&
    a.jitter === b.jitter
  );
});
